import 'react-datasheet-grid/dist/style.css'; // Needs to be imported once only
import { api } from '@packages/backend/convex/_generated/api';
import type { MenuCvx } from '@packages/shared';
import {
  categorySchema,
  getUserErrorMessage,
  menuItemSchema,
  modifierGroupSchema,
  modifierSchema,
} from '@packages/shared';
import { createFileRoute, Link, Outlet, redirect } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { EllipsisVertical } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { ActionButton } from '@/components/ui+/ActionButton';
import { BulkUpdateDescriptionsDialog } from './menu-items/components/BulkUpdateDescriptionsDialog';
import { BulkUploadImagesDialog } from './menu-items/components/BulkUploadImagesDialog';

const menuExportSchema = z.object({
  categories: z.array(categorySchema),
  menuItems: z.array(menuItemSchema),
  modifierGroups: z.array(modifierGroupSchema),
  modifiers: z.array(modifierSchema),
});

export const Route = createFileRoute('/r/$restaurantId/menu')({
  beforeLoad: async ({ location, params }) => {
    const { restaurantId } = params;
    if (location.pathname === `/r/${restaurantId}/menu`) {
      throw redirect({
        params: { restaurantId },
        to: '/r/$restaurantId/menu/menu-items',
      });
    }
  },
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const menuQuery = useQuery(api.menus.getByRestaurantId, { restaurantId });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImportWarning, setShowImportWarning] = useState(false);
  const [showBulkUploadImages, setShowBulkUploadImages] = useState(false);
  const [showBulkUpdateDescriptions, setShowBulkUpdateDescriptions] = useState(false);

  const handleExportMenu = (menu: MenuCvx) => {
    const stripped = {
      categories: menu.categories.map(({ createdAt, updatedAt, ...rest }) => rest),
      menuItems: menu.menuItems.map(({ createdAt, updatedAt, cdnImageKey, ...rest }) => rest),
      modifierGroups: menu.modifierGroups.map(({ createdAt, updatedAt, ...rest }) => rest),
      modifiers: menu.modifiers.map(({ createdAt, updatedAt, ...rest }) => rest),
    };

    const blob = new Blob([JSON.stringify(stripped, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'menu-export.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportMenuClick = () => {
    const menu = menuQuery.data;
    const hasExistingData =
      menu &&
      (menu.categories.length > 0 ||
        menu.menuItems.length > 0 ||
        menu.modifierGroups.length > 0 ||
        menu.modifiers.length > 0);

    if (hasExistingData) {
      setShowImportWarning(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so the same file can be re-selected
    e.target.value = '';

    const menu = menuQuery.data;
    if (!menu) {
      toast.error('No menu found for this restaurant');
      return;
    }

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const parsed = menuExportSchema.safeParse(json);

      if (!parsed.success) {
        toast.error('Invalid menu file', {
          description: parsed.error.issues.map((i) => i.message).join(', '),
        });
        return;
      }

      await convex.mutation(api.menus.importMenu, {
        menuId: menu._id,
        categories: parsed.data.categories,
        menuItems: parsed.data.menuItems,
        modifierGroups: parsed.data.modifierGroups,
        modifiers: parsed.data.modifiers,
      });

      toast.success('Menu imported successfully');
    } catch (error) {
      toast.error('Failed to import menu', {
        description: getUserErrorMessage(error),
      });
    }
  };

  return (
    <div className="p-2 md:p-6 min-h-[calc(100vh-(--spacing(16)))] flex-1 flex-col gap-4 bg-muted/60 dark:bg-muted/20">
      <div className="space-y-4 mb-4">
        <div className={'flex'}>
          <h2 className="text-2xl font-bold tracking-tight">Menu</h2>
          <SuperAdminOnly>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton className="ml-2 h-8 w-8" size="icon" variant="outline">
                  <EllipsisVertical className="h-3.5 w-3.5" />
                  <span className="sr-only">More</span>
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {menuQuery.data && (
                  <Link
                    params={{ menuId: menuQuery.data._id, restaurantId }}
                    target="_blank"
                    to={'/r/$restaurantId/menu/$menuId/versions'}
                  >
                    <DropdownMenuItem>View previous versions</DropdownMenuItem>
                  </Link>
                )}

                <DropdownMenuItem onSelect={handleImportMenuClick}>Import menu</DropdownMenuItem>

                {menuQuery.data && (
                  <DropdownMenuItem
                    onSelect={() => {
                      if (menuQuery.data) handleExportMenu(menuQuery.data);
                    }}
                  >
                    Export menu
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem onSelect={() => setShowBulkUploadImages(true)}>
                  Bulk upload images
                </DropdownMenuItem>

                <DropdownMenuItem onSelect={() => setShowBulkUpdateDescriptions(true)}>
                  Bulk update descriptions
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <input
              accept=".json"
              className="hidden"
              onChange={handleFileSelected}
              ref={fileInputRef}
              type="file"
            />

            <AlertDialog onOpenChange={setShowImportWarning} open={showImportWarning}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Replace existing menu?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will delete all current menu items, categories, and modifiers and replace
                    them with the imported menu.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => fileInputRef.current?.click()}>
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <BulkUploadImagesDialog
              isOpen={showBulkUploadImages}
              onOpenChange={setShowBulkUploadImages}
              restaurantId={restaurantId}
            />
            <BulkUpdateDescriptionsDialog
              isOpen={showBulkUpdateDescriptions}
              menuItems={menuQuery.data?.menuItems ?? []}
              onOpenChange={setShowBulkUpdateDescriptions}
              restaurantId={restaurantId}
            />
          </SuperAdminOnly>
        </div>

        <div className={'flex justify-between'}>
          <div>
            <div className="flex h-5 items-center space-x-4 text-sm">
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary/70',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/menu-items'}
              >
                Menu Items
              </Link>
              <Separator orientation="vertical" />
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary/70'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/modifiers'}
              >
                Modifiers
              </Link>
              <Separator orientation="vertical" />
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary/70'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/categories'}
              >
                Categories
              </Link>
              <Separator orientation="vertical" />
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary/70'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/overview'}
              >
                Overview
              </Link>
            </div>
          </div>
        </div>
        <Separator className="my-0" />
      </div>
      <Outlet />
    </div>
  );
}
